import React from 'react';
import { Box, Typography, keyframes, Paper } from '@mui/material';
import { useTransition } from '../../context/TransitionContext';
import images from '../../assets/images';

// --- KEYFRAMES ---
const cardPop = keyframes`
  0% { transform: scale(0) translate(var(--startX), var(--startY)); opacity: 0; }
  100% { transform: scale(1) translate(var(--startX), var(--startY)); opacity: 1; }
`;

const convergeToPlant = keyframes`
  0% { transform: scale(1) translate(var(--startX), var(--startY)); opacity: 1; border-radius: 12px; }
  100% { transform: scale(0.1) translate(var(--targetX), var(--targetY)); opacity: 0; border-radius: 50%; }
`;

const plantPartGrow = keyframes`
  0% { transform: scale(0); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`;

const globeScaleIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`;

const globeZoomOut = keyframes`
  0% { transform: scale(1); opacity: 1; filter: blur(0px); }
  100% { transform: scale(5); opacity: 0; filter: blur(10px); }
`;

const screenFadeOut = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; visibility: hidden; }
`;

const photoFadeIn = keyframes`
  0% { opacity: 0; transform: scale(1.1); }
  15%, 85% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.95); }
`;

const globeSpin = keyframes`
  0% { background-position: 47% 50%; } /* Africa Centered */
  100% { background-position: 147% 50%; }
`;

const cloudSpin = keyframes`
  0% { background-position: 50% 50%; }
  100% { background-position: 150% 50%; }
`;

const leafSway = keyframes`
  0%, 100% { transform: rotate(-2deg) scale(1); }
  50% { transform: rotate(2deg) scale(1.02); }
`;

const dropletGlimmer = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1) translateY(0); }
  50% { opacity: 0.9; transform: scale(1.1) translateY(2px); }
`;

const titleAnimate = keyframes`
  0% {
    opacity: 0;
    transform: translateY(15vh) scale(1.5);
    letter-spacing: 24px;
    color: #fff;
    text-shadow: 0 5px 15px rgba(0,0,0,0.3);
  }
  15%, 45% {
    opacity: 1;
    transform: translateY(15vh) scale(1.5);
    letter-spacing: 24px;
    color: #fff;
    text-shadow: 0 5px 15px rgba(0,0,0,0.3);
  }
  65%, 100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    letter-spacing: 12px;
    color: #2E7D32;
    text-shadow: 0 10px 20px rgba(0,0,0,0.1);
  }
`;

const subtitleFadeIn = keyframes`
  0%, 65% { opacity: 0; }
  85%, 100% { opacity: 0.8; }
`;

const plantFadeOut = keyframes`
  to {
    opacity: 0;
    transform: scale(0.75);
  }
`;

// --- DATA ---
const introPhotos = [
  images.farmField,
  images.heroBackground,
];

const counties = [
  { 
    name: 'Trans Nzoia', 
    activity: 'Ploughing the Future', 
    img: images.tractorSunset, 
    startX: '-22vw', startY: '-15vh', targetX: '0px', targetY: '120px', 
    delay: '2.1s', color: '#A1887F', part: 'Foundation'
  },
  { 
    name: 'Narok', 
    activity: 'Maasai Herdsmen', 
    img: images.sustainable, 
    startX: '22vw', startY: '-12vh', targetX: '0px', targetY: '40px', 
    delay: '2.3s', color: '#E53935', part: 'Strength'
  },
  { 
    name: 'Kericho', 
    activity: 'Harvesting Tea', 
    img: images.innovation, 
    startX: '-18vw', startY: '18vh', targetX: '-40px', targetY: '20px', 
    delay: '2.5s', color: '#388E3C', part: 'Growth'
  },
  { 
    name: 'Nakuru', 
    activity: 'Processing the Harvest', 
    img: images.analytics, 
    startX: '18vw', startY: '22vh', targetX: '40px', targetY: '-10px', 
    delay: '2.7s', color: '#546E7A', part: 'Production'
  },
  { 
    name: 'Uasin Gishu', 
    activity: 'Abundant Harvest', 
    img: images.wheat, 
    startX: '0vw', startY: '-25vh', targetX: '0px', targetY: '-40px', 
    delay: '2.9s', color: '#FB8C00', part: 'Bounty'
  },
];

const WaterDroplet: React.FC<{ top: number | string, left: number | string, size: number, delay?: string }> = ({ top, left, size, delay = '0s' }) => (
  <Box
    sx={{
      position: 'absolute',
      top,
      left,
      width: size,
      height: size * 1.3,
      borderRadius: '50% 50% 50% 50% / 70% 70% 30% 30%',
      background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.3) 40%, rgba(255,255,255,0) 70%)',
      backgroundColor: 'rgba(255,255,255,0.2)',
      boxShadow: 'inset -1px -1px 2px rgba(0,0,0,0.1), 1px 1px 3px rgba(255,255,255,0.4)',
      backdropFilter: 'blur(1px)',
      animation: `${dropletGlimmer} 3s ease-in-out ${delay} infinite`,
      zIndex: 10
    }}
  />
);

const LoadingScreen: React.FC<{ loading: boolean, onTransitionComplete: () => void }> = ({ loading, onTransitionComplete }) => {
  const { targetRect, startTransition, endTransition } = useTransition();
  const titleRef = React.useRef<HTMLDivElement>(null);
  const [mergePhase, setMergePhase] = React.useState(false);

  React.useEffect(() => {
    startTransition();
    setMergePhase(false);
  }, [startTransition]);

  // Calculate the dynamic transform to move the title to the navbar position
  const getDynamicTransform = () => {
    if (!targetRect || !titleRef.current) return '';

    const currentRect = titleRef.current.getBoundingClientRect();
    
    // Calculate the difference in position
    const deltaX = targetRect.left - currentRect.left;
    const deltaY = targetRect.top - currentRect.top;
    
    // Calculate the scale difference
    const scale = targetRect.width / currentRect.width;

    return `translate(${deltaX}px, ${deltaY}px) scale(${scale})`;
  };

  React.useEffect(() => {
    if (!loading) {
      if (targetRect) setMergePhase(true);
      
      const timer = setTimeout(() => {
        endTransition();
        onTransitionComplete();
      }, 1200);

      return () => clearTimeout(timer);
    }
  }, [loading, targetRect, endTransition, onTransitionComplete]);

  

  // Title styling journey: 
  // 1. Initial keyframe animation (Intro -> Above Globe)
  // 2. Dynamic transition (Above Globe -> Navbar merge)
  const titleStyle = mergePhase && targetRect ? {
    transform: getDynamicTransform(),
    transformOrigin: 'top left',
    transition: 'all 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)', // Nice overshoot effect
    color: '#fff', 
    letterSpacing: 'normal', 
    textShadow: 'none',
    zIndex: 1000000,
  } : {
    animation: `${titleAnimate} 4s cubic-bezier(0.25, 1, 0.5, 1) 0.5s forwards`
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 999999,
        overflow: 'hidden',
        pointerEvents: loading ? 'all' : 'none'
      }}
    >
      {/* BACKGROUND LAYER - Fades out at the end */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: '#ffffff',
          animation: `${screenFadeOut} 0.8s ease-in 19.5s forwards`,
          zIndex: 0
        }}
      />

      {/* PHASE 0: Agricultural Intro Photos */}
      {introPhotos.map((photo, index) => (
        <Box
          key={index}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${photo})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0,
            animation: `${photoFadeIn} 2.5s ease-in-out ${index * 1.2}s forwards`,
            zIndex: 1
          }}
        >
          <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.5))' }} />
        </Box>
      ))}

      {/* FINAL BRANDING - MOVED TO TOP */}
      <Box 
        sx={{ 
          position: 'absolute', 
          top: '10%', 
          textAlign: 'center',
          zIndex: 20,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Typography 
          ref={titleRef}
          variant="h2" 
          sx={{ 
            fontWeight: 900, 
            fontFamily: "'Poppins', sans-serif",
            textTransform: 'uppercase',
            mb: 1,
            fontSize: { xs: '2.5rem', md: '3.75rem' },
            opacity: 0,
            ...titleStyle
          }}
        >
          Farmers HUB
        </Typography>
        <Typography variant="h5" sx={{ 
          color: '#444', 
          fontWeight: 700, 
          letterSpacing: '4px',
          textTransform: 'uppercase',
          opacity: 0,
          fontSize: { xs: '1rem', md: '1.5rem' },
          animation: `${subtitleFadeIn} 3.5s ease-in 0.5s forwards`,
          transition: 'opacity 0.5s',
          ...(mergePhase && { opacity: 0, animation: 'none' }) // Fade out subtitle during merge
        }}>
          Uniting Kenya's Bounty
        </Typography>
      </Box>

      {/* WRAPPER FOR CONTENT THAT FADES OUT (Globe, Plant, Cards) */}
      <Box sx={{ 
        position: 'absolute', 
        inset: 0, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        animation: `${screenFadeOut} 0.8s ease-in 19.5s forwards`,
        zIndex: 5
      }}>
        {/* PHASE 1 & 2: Distributed County Cards */}
        {counties.map((county, index) => (
          <Paper
            key={index}
            elevation={12}
            sx={{
              position: 'absolute',
              width: { xs: 300, md: 480 }, 
              height: { xs: 200, md: 320 }, 
              overflow: 'hidden',
              borderRadius: '24px',
              border: `6px solid ${county.color}`,
              '--startX': county.startX,
              '--startY': county.startY,
              '--targetX': county.targetX,
              '--targetY': county.targetY,
              animation: `
                ${cardPop} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${county.delay} forwards,
                ${convergeToPlant} 0.8s ease-in-out 3.8s forwards
              `,
              opacity: 0,
              zIndex: 10,
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                backgroundImage: `url(${county.img})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                p: 3,
                position: 'relative'
              }}
            >
              <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.6))' }} />
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 900, zIndex: 1, lineHeight: 1.1, textShadow: '0 4px 8px rgba(0,0,0,0.6)', fontSize: { xs: '1.25rem', md: '2.125rem' } }}>
                {county.name}
              </Typography>
              <Typography variant="h6" sx={{ color: '#E8F5E9', fontWeight: 600, zIndex: 1, mt: 1, opacity: 0.9, fontSize: { xs: '0.875rem', md: '1.25rem' } }}>
                {county.activity}
              </Typography>
              <Box sx={{ 
                position: 'absolute', top: 20, right: 20, px: 2, py: 1, 
                bgcolor: county.color, borderRadius: 2, zIndex: 1,
                boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
              }}>
                <Typography variant="button" sx={{ color: '#fff', fontWeight: 'bold', fontSize: { xs: '0.625rem', md: '0.875rem' } }}>
                  {county.part}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))}

        {/* PHASE 3: The Unified Plant Growth (Lush Sprout with Wet Leaves) */}
        <Box sx={{ 
          position: 'relative', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          zIndex: 5,
          mt: 12,
          animation: `${plantFadeOut} 0.8s ease-in 8.2s forwards`
        }}>
          
          {/* Lush Sprout Leaves (Broader and healthier) */}
          <Box sx={{ position: 'relative', width: '100%', height: 0 }}>
            {/* Main Top Left Leaf */}
            <Box sx={{ 
              position: 'absolute', right: 0, bottom: -10,
              width: 120, height: 160, 
              background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
              borderRadius: '100% 10% 100% 10% / 100% 10% 100% 10%', 
              transformOrigin: 'bottom right',
              transform: 'rotate(-15deg) scale(0)',
              animation: `
                ${plantPartGrow} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 4.4s forwards,
                ${leafSway} 5s ease-in-out 5.1s infinite
              `,
              opacity: 0,
              boxShadow: 'inset -15px 15px 30px rgba(0,0,0,0.3), 0 10px 20px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              <WaterDroplet top="15%" left="25%" size={14} delay="0s" />
              <WaterDroplet top="45%" left="55%" size={10} delay="1s" />
              <WaterDroplet top="70%" left="15%" size={12} delay="0.5s" />
              <Box sx={{ position: 'absolute', top: 0, right: 0, width: '100%', height: '100%', borderRight: '3px solid rgba(255,255,255,0.15)', borderRadius: 'inherit' }} />
            </Box>

            {/* Main Top Right Leaf */}
            <Box sx={{ 
              position: 'absolute', left: 0, bottom: -10,
              width: 120, height: 160, 
              background: 'linear-gradient(-135deg, #66BB6A 0%, #388E3C 100%)',
              borderRadius: '10% 100% 10% 100% / 10% 100% 10% 100%', 
              transformOrigin: 'bottom left',
              transform: 'rotate(15deg) scale(0)',
              animation: `
                ${plantPartGrow} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 4.6s forwards,
                ${leafSway} 5s ease-in-out 5.3s infinite alternate
              `,
              opacity: 0,
              boxShadow: 'inset 15px 15px 30px rgba(0,0,0,0.3), 0 10px 20px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              <WaterDroplet top="10%" left="55%" size={12} delay="0.2s" />
              <WaterDroplet top="40%" left="20%" size={16} delay="1.2s" />
              <WaterDroplet top="65%" left="65%" size={10} delay="0.7s" />
              <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderLeft: '3px solid rgba(255,255,255,0.15)', borderRadius: 'inherit' }} />
            </Box>
          </Box>

          {/* Stem */}
          <Box
            sx={{
              width: 18, height: 140, 
              background: 'linear-gradient(to bottom, #4CAF50, #1B5E20)',
              borderRadius: '10px',
              animation: `${plantPartGrow} 0.8s ease-out 4.2s forwards`, opacity: 0,
              boxShadow: '2px 0 8px rgba(0,0,0,0.2)'
            }}
          />

          {/* Soil/Root */}
          <Box
            sx={{
              width: 220, height: 20, 
              background: 'radial-gradient(circle, #5D4037 60%, #3E2723 100%)',
              borderRadius: '30px / 15px', mt: -2,
              animation: `${plantPartGrow} 0.6s ease-out 4.0s forwards`, opacity: 0,
              boxShadow: '0 8px 15px rgba(0,0,0,0.3)'
            }}
          />
        </Box>

        {/* PHASE 4: The Spinning Realistic Globe Finale */}
        <Box
          sx={{
            position: 'absolute',
            width: { xs: 400, md: 600 }, 
            height: { xs: 400, md: 600 }, 
            opacity: 0,
            zIndex: 100, // Very high z-index to be on top
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '50%',
            overflow: 'hidden',
            backgroundColor: '#1a237e', // Fallback color (Indigo)
            border: '2px solid rgba(255,255,255,0.2)', // Subtle border
            boxShadow: `
              inset 0 0 100px rgba(0,0,0,0.9),
              0 0 100px rgba(33, 150, 243, 0.5)
            `,
            backgroundImage: 'url("/earth_daymap.jpg")',
            backgroundSize: '200% 100%',
            backgroundPosition: '47% 50%', 
            backgroundRepeat: 'repeat-x',
            willChange: 'background-position, transform', 
            animation: `
              ${globeScaleIn} 1.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) 5.5s forwards, 
              ${globeSpin} 40s linear 7s infinite,
              ${globeZoomOut} 1.5s cubic-bezier(0.6, -0.28, 0.735, 0.045) 17.5s forwards
            `,
            '&::after': { 
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundImage: 'url("/earth_clouds.jpg")',
              backgroundSize: '200% 100%',
              opacity: 0.25,
              mixBlendMode: 'screen',
              willChange: 'background-position', 
              animation: `${cloudSpin} 60s linear infinite`,
              pointerEvents: 'none'
            },
            '&::before': { 
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.15) 0%, transparent 60%)',
              zIndex: 101
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default LoadingScreen;
